import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, setDoc, where } from 'firebase/firestore';
import { MapContainer, TileLayer, CircleMarker, useMap, Polyline, Popup, ZoomControl, Tooltip, Marker } from 'react-leaflet';
import {
  Star, MapPin, Navigation, ExternalLink, ChevronDown, ChevronUp,
  Clock, Cloud, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun,
  Layout, Layers, Map as MapIcon, Sunrise, Sunset, Share2,
  CheckCircle2, Bird, TreePine, Gavel, Loader2, ArrowLeft, Route, X, Sun, ChevronLeft, ChevronRight, User, Globe
} from 'lucide-react';
import LegalGuide from './LegalGuide';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

import { db, getCachedSpotsInArea, translateSpotContent } from '../services/firebaseService';
import { createCustomIcon } from './map/MapUtils';
import { CampingSpot, UserReview, HikingTrail, ElevationPoint, SearchResult, Coordinates, WikiSpot } from '../types';
import { MAP_LAYERS } from '../constants';
import { reverseGeocode } from '../services/geocodingService';
import { fetchAttractions, fetchWikiTranslations } from '../services/wikiService';
import { useWildlifeLayer, WildlifeMapObservation } from '../hooks/useWildlifeLayer';
import { fetchNearbyHikingTrails } from '../services/hikingService';
import { generateSpotName, generateSpotShareSummary, generateTrailName } from '../services/geminiService';
import { shouldAutoGenerateSpotDescription } from '../services/spotIdentity';
import * as SunCalc from 'suncalc';
import { fetchElevation } from '../services/elevationService';
import { getDistance } from '../services/geometryUtils';
import ElevationProfile from './routes/ElevationProfile';
import L from 'leaflet';
import { fetchSnowDepth, SnowDepthResult } from '../services/snowService';

import { BRAND } from '../constants/mapColors';
interface SharedReviewPageProps {
  reviewId?: string;
  spotId?: string;
}

type MediaSlide =
  | { type: 'image'; url: string; creditName?: string; creditPhoto?: string; creditDate?: string }
  | { type: 'video-embed'; url: string }
  | { type: 'video-file'; url: string }
  | { type: 'attraction-image'; url: string; link: string; title: string };

interface WeatherDay {
  key: string;
  label: string;
  maxTemp: number;
  minTemp: number;
  symbol: string;
}

interface TrailWithCoords {
  trail: HikingTrail;
  coords: [number, number][];
}

const MapSizeFixer: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const fix = () => {
      if (!map) return;
      map.invalidateSize({ animate: false, pan: false });
      const c = map.getCenter();
      const z = map.getZoom();
      if (c && z != null) map.setView(c, z, { animate: false });
    };

    // Use whenReady to fire after Leaflet finishes its own init
    map.whenReady(() => {
      // Small delay to let the browser paint the container at full size
      setTimeout(fix, 50);
      setTimeout(fix, 300);
      setTimeout(fix, 800);
    });

    // Also watch for container resize (handles CSS animations finishing)
    let ro: ResizeObserver | null = null;
    const container = map.getContainer();
    if (typeof ResizeObserver !== 'undefined' && container) {
      ro = new ResizeObserver(() => requestAnimationFrame(fix));
      ro.observe(container);
    }
    window.addEventListener('resize', fix);

    return () => {
      window.removeEventListener('resize', fix);
      if (ro) ro.disconnect();
    };
  }, [map]);
  return null;
};

const FitDataBounds: React.FC<{
  points: [number, number][];
  polylines: [number, number][][];
}> = ({ points, polylines }) => {
  const map = useMap();
  const [lastKey, setLastKey] = useState<string>('');

  useEffect(() => {
    const allCoords: [number, number][] = [];
    points.forEach((p) => allCoords.push(p));
    polylines.forEach((line) => line.forEach((p) => allCoords.push(p)));

    if (allCoords.length === 0) return;

    const key = `${allCoords.length}:${allCoords[0]?.join(',')}:${allCoords[allCoords.length - 1]?.join(',')}`;
    if (key === lastKey) return;
    setLastKey(key);

    const lats = allCoords.map((c) => c[0]);
    const lngs = allCoords.map((c) => c[1]);
    const south = Math.min(...lats);
    const north = Math.max(...lats);
    const west = Math.min(...lngs);
    const east = Math.max(...lngs);

    map.fitBounds(
      [
        [south, west],
        [north, east]
      ],
      { padding: [42, 42], maxZoom: 15 }
    );
  }, [lastKey, map, points, polylines]);

  return null;
};

const SharedReviewPage: React.FC<SharedReviewPageProps> = ({ reviewId, spotId }) => {
  const [review, setReview] = useState<UserReview | null>(null);
  const [spot, setSpot] = useState<CampingSpot | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLegal, setShowLegal] = useState(false);
  const [address, setAddress] = useState<string>('');
  const [attractions, setAttractions] = useState<WikiSpot[]>([]);
  const [loadingAttractions, setLoadingAttractions] = useState(false);
  const [mapType, setMapType] = useState<'topo' | 'satellite' | 'dark'>('satellite');
  const [showMapLayers, setShowMapLayers] = useState({ spot: true, wildlife: true, trails: true });
  const [activeInfoTab, setActiveInfoTab] = useState<'wildlife' | 'trails' | 'attractions'>('wildlife');
  const [expandedWildlifeIds, setExpandedWildlifeIds] = useState<Set<string>>(new Set());
  const [expandedTrailIds, setExpandedTrailIds] = useState<Set<string>>(new Set());
  const [expandedAttractionIds, setExpandedAttractionIds] = useState<Set<number>>(new Set());
  const [fallbackTrails, setFallbackTrails] = useState<HikingTrail[]>([]);
  const [loadingFallbackTrails, setLoadingFallbackTrails] = useState(false);
  const [weather7d, setWeather7d] = useState<WeatherDay[]>([]);
  const { t, i18n: i18nHook } = useTranslation();

  const currentLanguage = i18nHook.language || 'no';
  const appLang = useMemo(() => {
    const raw = currentLanguage.toLowerCase();
    if (raw.startsWith('en')) return 'en';
    if (raw.startsWith('de')) return 'de';
    return 'no';
  }, [currentLanguage]);
  const withLang = useCallback((path: string) => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('lang', appLang);
    return `${url.pathname}${url.search}`;
  }, [appLang]);
  const fricampWebUrl = useMemo(() => {
    const url = new URL('https://www.fricamp.com/');
    url.searchParams.set('lang', appLang);
    return url.toString();
  }, [appLang]);
  const mobileOS = useMemo(() => {
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';
    if (/android/i.test(ua)) return 'android';
    return null;
  }, []);
  const appStoreUrl = mobileOS === 'ios'
    ? 'https://apps.apple.com/no/app/fricamp/id6759742331'
    : mobileOS === 'android'
      ? 'https://play.google.com/store/apps/details?id=com.takleconsulting.fricamp'
      : null;

  const toggleLanguage = () => {
    const nextLang = currentLanguage.startsWith('no') ? 'en' : 'no';
    i18nHook.changeLanguage(nextLang);
  };

  const [weatherLoading, setWeatherLoading] = useState(false);
  const [sunData, setSunData] = useState<{ sunrise: string; sunset: string; daylightHours: number } | null>(null);
  const [snowData, setSnowData] = useState<SnowDepthResult | null>(null);
  const [routeMeta, setRouteMeta] = useState<{ roadType: string; surface: string }>({ roadType: '', surface: '' });
  const [trailInfoId, setTrailInfoId] = useState<string | null>(null);
  const [trailElevationById, setTrailElevationById] = useState<Record<string, ElevationPoint[]>>({});
  const [loadingTrailElevationId, setLoadingTrailElevationId] = useState<string | null>(null);
  const [spotRating, setSpotRating] = useState<{ avg: number; count: number }>({ avg: 0, count: 0 });
  const [verificationMeta, setVerificationMeta] = useState<{ by?: string; when?: string }>({});
  const [displaySpotName, setDisplaySpotName] = useState<string>('');
  const [reviewPhotoCredits, setReviewPhotoCredits] = useState<Record<string, { userName?: string; userPhoto?: string; createdAt?: string }>>({});
  const [reviewPhotoUrls, setReviewPhotoUrls] = useState<string[]>([]);
  const [reviewVideoUrls, setReviewVideoUrls] = useState<string[]>([]);
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [mediaPaused, setMediaPaused] = useState(false);
  const swipeStartXRef = useRef<number | null>(null);
  const namedTrailSpotIdsRef = useRef<Set<string>>(new Set());
  const summarySpotIdsRef = useRef<Set<string>>(new Set());
  const nameSpotIdsRef = useRef<Set<string>>(new Set());
  const [mapKey, setMapKey] = useState(0);
  const [nearbyAmenities, setNearbyAmenities] = useState<CampingSpot[]>([]);

  // --- Content translation state ---
  const [translatedSpotName, setTranslatedSpotName] = useState<string | null>(null);
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [translatedReviewComment, setTranslatedReviewComment] = useState<string | null>(null);
  const [translatedAttractions, setTranslatedAttractions] = useState<Map<number, WikiSpot>>(new Map());
  const [contentTranslating, setContentTranslating] = useState(false);
  // Tracks which language the current translations are for (null = not yet translated)
  const [translatedForLang, setTranslatedForLang] = useState<string | null>(null);

  // Auto-translate content whenever the UI language differs from what we have translated.
  // Always translates to the current appLang (including 'no'), so English-sourced content
  // is properly translated when the user switches to Norwegian and vice versa.
  useEffect(() => {
    if (!spot || contentTranslating) return;
    if (translatedForLang === appLang) return;
    handleTranslateAll();
  }, [appLang, spot?.id, translatedForLang, contentTranslating]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTranslateAll = async () => {
    if (!spot || contentTranslating) return;
    const targetLang = appLang; // capture at call time in case language changes mid-flight
    setContentTranslating(true);
    const spotName = displaySpotName || spot.name || '';
    const description = spot.aiResult?.description || spot.description || '';
    const reviewText = review?.comment || '';

    try {
      const [spotResult, wikiResult] = await Promise.allSettled([
        (spotName || description || reviewText)
          ? translateSpotContent(spot.id, targetLang, spotName, description, reviewText)
          : Promise.resolve(null),
        // Wiki attractions are sourced from no.wikipedia.org, so no translation needed for Norwegian
        attractions.length > 0 && targetLang !== 'no'
          ? fetchWikiTranslations(attractions, targetLang as 'en' | 'de')
          : Promise.resolve(new Map<number, WikiSpot>())
      ]);

      if (spotResult.status === 'fulfilled' && spotResult.value?.ok && spotResult.value.data) {
        const d = spotResult.value.data;
        if (d.spotName) setTranslatedSpotName(d.spotName);
        if (d.spotDescription) setTranslatedDescription(d.spotDescription);
        if (d.reviewComment) setTranslatedReviewComment(d.reviewComment);
      } else {
        setTranslatedSpotName(null);
        setTranslatedDescription(null);
        setTranslatedReviewComment(null);
      }

      if (wikiResult.status === 'fulfilled' && wikiResult.value) {
        setTranslatedAttractions(wikiResult.value);
      } else {
        setTranslatedAttractions(new Map());
      }
    } catch {
      /* keep original text on failure */
    } finally {
      setTranslatedForLang(targetLang);
      setContentTranslating(false);
    }
  };

  const getSpotDescriptionFallback = (s: CampingSpot | null) => {
    if (!s) return t('shared_spot.description_coming');
    const road = s.highway || t('shared_spot.unknown_road');
    const surface = (s.surfaceType || t('shared_spot.unknown_surface')).toLowerCase();
    const nearestHouse = typeof s.nearestHouse === 'number'
      ? t('shared_spot.distance_to_settlement', { meters: Math.round(s.nearestHouse) })
      : t('shared_spot.unknown_distance');
    return t('shared_spot.algorithmic_description', { road, surface, nearestHouse });
  };

  // Force Leaflet to re-initialize after the container has been painted at full size
  useEffect(() => {
    const t = setTimeout(() => setMapKey(1), 500);
    return () => clearTimeout(t);
  }, []);

  // Logic for wildlife layer
  const center = useMemo(() => {
    if (spot?.location) return spot.location;
    return { latitude: 61.1373, longitude: 8.9478 };
  }, [spot?.location]);

  const viewBounds = useMemo(() => ({
    latMin: center.latitude - 0.25,
    latMax: center.latitude + 0.25,
    lonMin: center.longitude - 0.35,
    lonMax: center.longitude + 0.35
  }), [center.latitude, center.longitude]);
  const { wildlife: wildlifePoints } = useWildlifeLayer(true, 13, center, viewBounds);

  const groupedWildlife = useMemo(() => {
    const byKey = new Map<string, WildlifeMapObservation[]>();
    wildlifePoints.forEach((w) => {
      // Defensive: useWildlifeLayer filters at source, but render-time access
      // of w.location.latitude must never throw — guard explicitly.
      if (!w?.location || !Number.isFinite(w.location.latitude) || !Number.isFinite(w.location.longitude)) return;
      const key = `${w.location.latitude.toFixed(3)}_${w.location.longitude.toFixed(3)}`;
      const existing = byKey.get(key) || [];
      existing.push(w);
      byKey.set(key, existing);
    });
    return Array.from(byKey.values()).map((members) => {
      const first = members[0];
      return {
        id: members.map(m => m.id).join('_'),
        location: first.location,
        members,
        count: members.length
      };
    });
  }, [wildlifePoints]);

  const mergedTrails = useMemo<HikingTrail[]>(() => {
    const combined = [...(spot?.hikingTrails || []), ...(fallbackTrails || [])];
    const seen = new Set<string>();
    const out: HikingTrail[] = [];
    combined.forEach((trail) => {
      const id = String(trail.id || '');
      const name = String(trail.name || '').trim().toLowerCase();
      const key = id || `${name}:${Math.round((trail.distanceToStart || 0) / 50)}`;
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(trail);
    });
    return out;
  }, [fallbackTrails, spot?.hikingTrails]);

  const trailsWithGeometry = useMemo<TrailWithCoords[]>(() => {
    const source = mergedTrails;
    return source
      .map((trail) => ({ trail, coords: extractTrailCoords(trail) }))
      .filter((row) => row.coords.length > 1);
  }, [mergedTrails]);

  const allNearbyTrails = useMemo(() => {
    return mergedTrails || [];
  }, [mergedTrails]);

  const wildlifeCount = wildlifePoints.length;
  const trailsCount = allNearbyTrails.length;
  const attractionsCount = attractions.length;
  const spotScore = Math.round(spot?.aiResult?.score ?? spot?.score ?? 0);

  // Effective translated values (translated if available, otherwise original)
  const effectiveSpotName = translatedSpotName || displaySpotName || t('common.location');
  const effectiveDescription = translatedDescription || String(spot?.aiResult?.description || spot?.description || '');
  const effectiveReviewComment = translatedReviewComment || review?.comment || '';
  const getEffectiveWiki = (attr: WikiSpot) => translatedAttractions.get(attr.pageid) || attr;

  const autoFitPoints = useMemo<[number, number][]>(() => {
    const pts: [number, number][] = [];
    if (spot?.location && Number.isFinite(spot.location.latitude) && Number.isFinite(spot.location.longitude)) {
      pts.push([spot.location.latitude, spot.location.longitude]);
    }
    groupedWildlife.forEach((g) => {
      if (g?.location && Number.isFinite(g.location.latitude) && Number.isFinite(g.location.longitude)) {
        pts.push([g.location.latitude, g.location.longitude]);
      }
    });
    return pts;
  }, [groupedWildlife, spot?.location]);

  const autoFitPolylines = useMemo<[number, number][][]>(() => {
    return trailsWithGeometry.map((row) => row.coords);
  }, [trailsWithGeometry]);

  const activeTrailInfo = useMemo(() => {
    if (!trailInfoId) return null;
    return trailsWithGeometry.find((row) => row.trail.id === trailInfoId) || null;
  }, [trailInfoId, trailsWithGeometry]);

  const activeTrailProfile = useMemo(() => {
    if (!activeTrailInfo) return undefined;
    return trailElevationById[activeTrailInfo.trail.id];
  }, [activeTrailInfo, trailElevationById]);

  const activeTrailStart = useMemo<[number, number] | null>(() => {
    if (!activeTrailInfo || activeTrailInfo.coords.length < 2) return null;
    return activeTrailInfo.coords[0];
  }, [activeTrailInfo]);

  const activeTrailEnd = useMemo<[number, number] | null>(() => {
    if (!activeTrailInfo || activeTrailInfo.coords.length < 2) return null;
    return activeTrailInfo.coords[activeTrailInfo.coords.length - 1];
  }, [activeTrailInfo]);

  const activeTrailPeak = useMemo<[number, number] | null>(() => {
    if (!activeTrailInfo || !activeTrailProfile || activeTrailProfile.length < 3) return null;
    let maxIdx = 0;
    for (let i = 1; i < activeTrailProfile.length; i++) {
      if (activeTrailProfile[i].elevation > activeTrailProfile[maxIdx].elevation) maxIdx = i;
    }
    if (maxIdx === 0 || maxIdx === activeTrailProfile.length - 1) return null;
    const coordIdx = Math.round((maxIdx / (activeTrailProfile.length - 1)) * (activeTrailInfo.coords.length - 1));
    if (coordIdx <= 0 || coordIdx >= activeTrailInfo.coords.length - 1) return null;
    return activeTrailInfo.coords[coordIdx];
  }, [activeTrailInfo, activeTrailProfile]);

  useEffect(() => {
    if (!showMapLayers.trails) setTrailInfoId(null);
  }, [showMapLayers.trails]);

  const handleSelectInfoTab = (tab: 'wildlife' | 'trails' | 'attractions') => {
    setActiveInfoTab(tab);
    if (tab === 'wildlife') {
      if (wildlifePoints.length > 0 && wildlifePoints.length <= 10) {
        setExpandedWildlifeIds(new Set([wildlifePoints[0].id]));
      } else {
        setExpandedWildlifeIds(new Set());
      }
      return;
    }
    if (tab === 'trails') {
      if (allNearbyTrails.length > 0 && allNearbyTrails.length <= 10) {
        setExpandedTrailIds(new Set([allNearbyTrails[0].id]));
      } else {
        setExpandedTrailIds(new Set());
      }
      return;
    }
    if (attractions.length > 0 && attractions.length <= 10) {
      setExpandedAttractionIds(new Set([attractions[0].pageid]));
    } else {
      setExpandedAttractionIds(new Set());
    }
  };

  useEffect(() => {
    const run = async () => {
      if (!activeTrailInfo || !trailInfoId) return;
      if (trailElevationById[trailInfoId]) return;
      if (activeTrailInfo.coords.length < 2) return;

      setLoadingTrailElevationId(trailInfoId);
      try {
        const sampleCount = Math.min(22, activeTrailInfo.coords.length);
        const sampled: [number, number][] = [];
        for (let i = 0; i < sampleCount; i++) {
          const idx = Math.round((i / (sampleCount - 1 || 1)) * (activeTrailInfo.coords.length - 1));
          sampled.push(activeTrailInfo.coords[idx]);
        }

        const elevations = await Promise.all(
          sampled.map(async (p) => {
            const ele = await fetchElevation({ latitude: p[0], longitude: p[1] });
            return ele;
          })
        );

        const profile: ElevationPoint[] = [];
        let cumulativeKm = 0;
        for (let i = 0; i < sampled.length; i++) {
          if (i > 0) {
            cumulativeKm += getDistance(sampled[i - 1][0], sampled[i - 1][1], sampled[i][0], sampled[i][1]) / 1000;
          }
          profile.push({
            distance: Number(cumulativeKm.toFixed(2)),
            elevation: typeof elevations[i] === 'number'
              ? elevations[i]!
              : (i > 0 ? profile[i - 1].elevation : 0)
          });
        }
        if (profile.length >= 2) {
          setTrailElevationById((prev) => ({ ...prev, [trailInfoId]: profile }));
        }
      } catch (e) {
        console.warn('Kunne ikke bygge høydeprofil i delingsvisning', e);
      } finally {
        setLoadingTrailElevationId((prev) => (prev === trailInfoId ? null : prev));
      }
    };
    run();
  }, [activeTrailInfo, trailInfoId, trailElevationById]);

  useEffect(() => {
    // Override app-level scroll lock while shared page is open.
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');

    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowY: html.style.overflowY,
      htmlHeight: html.style.height,
      bodyOverflow: body.style.overflow,
      bodyOverflowY: body.style.overflowY,
      bodyHeight: body.style.height,
      rootHeight: root?.style.height || '',
      rootOverflowY: root?.style.overflowY || ''
    };

    html.style.overflow = 'auto';
    html.style.overflowY = 'auto';
    html.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.overflowY = 'auto';
    body.style.height = 'auto';
    if (root) {
      root.style.height = 'auto';
      root.style.overflowY = 'auto';
    }

    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowY = prev.htmlOverflowY;
      html.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowY = prev.bodyOverflowY;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.height = prev.rootHeight;
        root.style.overflowY = prev.rootOverflowY;
      }
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        console.log('DEBUG: SharedReviewPage loading ID:', reviewId || spotId);
        if (reviewId) {
          const reviewRef = doc(db, 'reviews', reviewId);
          const reviewSnap = await getDoc(reviewRef);

          if (reviewSnap.exists()) {
            const fetchedReview = { id: reviewSnap.id, ...reviewSnap.data() } as UserReview;
            setReview(fetchedReview);

            if (fetchedReview.spotId) {
              // First try spots, then cached_spots for more technical data
              let fetchedSpot: CampingSpot | null = null;

              const spotRef = doc(db, 'spots', fetchedReview.spotId);
              const spotSnap = await getDoc(spotRef);
              if (spotSnap.exists()) {
                fetchedSpot = { id: spotSnap.id, ...spotSnap.data() } as CampingSpot;
              }

              const cachedRef = doc(db, 'cached_spots', fetchedReview.spotId);
              const cachedSnap = await getDoc(cachedRef);
              if (cachedSnap.exists()) {
                const cachedData = cachedSnap.data() as CampingSpot;
                fetchedSpot = { ...(fetchedSpot || {}), ...cachedData, id: cachedSnap.id } as CampingSpot;
              }

              if (fetchedSpot) {
                setSpot(fetchedSpot);
                console.log('DEBUG: Final Spot data merged:', fetchedSpot);

                // Resolve address
                try {
                  const addr = await getDetailedAddress(fetchedSpot.location);
                  setAddress(addr);
                } catch (e) {
                  console.warn('Geocoding failed', e);
                  if (fetchedSpot.location) {
                    const fallbackShort = await reverseGeocode(fetchedSpot.location);
                    setAddress(fallbackShort || `${fetchedSpot.location.latitude.toFixed(5)}, ${fetchedSpot.location.longitude.toFixed(5)}`);
                  }
                }
              }
            }
          }
        } else if (spotId) {
          // Direct spot load
          let fetchedSpot: CampingSpot | null = null;
          const spotRef = doc(db, 'spots', spotId);
          const spotSnap = await getDoc(spotRef);
          if (spotSnap.exists()) {
            fetchedSpot = { id: spotSnap.id, ...spotSnap.data() } as CampingSpot;
          }
          const cachedRef = doc(db, 'cached_spots', spotId);
          const cachedSnap = await getDoc(cachedRef);
          if (cachedSnap.exists()) {
            const cachedData = cachedSnap.data() as CampingSpot;
            fetchedSpot = { ...(fetchedSpot || {}), ...cachedData, id: cachedSnap.id } as CampingSpot;
          }
          if (fetchedSpot) {
            setSpot(fetchedSpot);
            // Resolve address
            try {
              const addr = await getDetailedAddress(fetchedSpot.location);
              setAddress(addr);
            } catch (e) {
              console.warn('Geocoding failed', e);
              if (fetchedSpot.location) {
                const fallbackShort = await reverseGeocode(fetchedSpot.location);
                setAddress(fallbackShort || `${fetchedSpot.location.latitude.toFixed(5)}, ${fetchedSpot.location.longitude.toFixed(5)}`);
              }
            }
          }
        }
      } catch (err) {
        console.error('DEBUG: Load Error:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reviewId, spotId]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.location) return;
      setLoadingAttractions(true);
      try {
        const results = await fetchAttractions(spot.location);
        setAttractions(results.slice(0, 6));
      } catch (e) {
        console.warn('Attraction fetch failed', e);
        setAttractions([]);
      } finally {
        setLoadingAttractions(false);
      }
    };

    run();
  }, [spot?.id]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.id) return;
      try {
        const q = query(collection(db, 'reviews'), where('spotId', '==', spot.id));
        const snap = await getDocs(q);
        const ratings = snap.docs
          .map((d) => Number((d.data() as any)?.rating))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (ratings.length === 0) {
          setSpotRating({ avg: 0, count: 0 });
          setVerificationMeta({});
          return;
        }
        const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
        setSpotRating({ avg, count: ratings.length });

        const photoCredits: Record<string, { userName?: string; userPhoto?: string; createdAt?: string }> = {};
        const urls: string[] = [];
        const videoUrls: string[] = [];
        snap.docs.forEach((d) => {
          const data = d.data() as any;
          const url = (data?.photoUrl || '').trim();
          const videoUrl = String(data?.videoUrl || data?.videoURL || data?.video || data?.video_link || data?.videoLink || '').trim();
          if (videoUrl) videoUrls.push(videoUrl);
          if (!url) return;
          urls.push(url);
          if (!photoCredits[url]) {
            const createdMs = normalizeTimestampMs(data?.createdAt);
            photoCredits[url] = {
              userName: data?.userName || data?.createdByName || t('route.anonymous'),
              userPhoto: data?.userPhoto || data?.createdByPhoto || undefined,
              createdAt: Number.isFinite(createdMs) ? new Date(createdMs).toLocaleDateString(getUiLocale()) : inferDateFromStorageLikeUrl(url)
            };
          }
        });
        setReviewPhotoCredits(photoCredits);
        setReviewPhotoUrls(Array.from(new Set(urls)));
        setReviewVideoUrls(Array.from(new Set(videoUrls)));

        if (spot.isVerified && snap.docs.length > 0) {
          const sorted = snap.docs
            .map((d) => d.data() as any)
            .sort((a, b) => normalizeTimestampMs(a.createdAt) - normalizeTimestampMs(b.createdAt));
          const first = sorted[0];
          const who = first?.userName || first?.createdByName;
          const whenMs = normalizeTimestampMs(first?.createdAt);
          setVerificationMeta({
            by: who,
            when: Number.isFinite(whenMs) ? new Date(whenMs).toLocaleDateString(getUiLocale()) : undefined
          });
        } else {
          setVerificationMeta({});
        }
      } catch (e) {
        console.warn('Could not load spot ratings', e);
      }
    };
    run();
  }, [spot?.id, spot?.isVerified]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.location) return;
      if (spot.hikingTrails && spot.hikingTrails.length > 0) {
        setFallbackTrails([]);
        return;
      }
      setLoadingFallbackTrails(true);
      try {
        const trails = await fetchNearbyHikingTrails(spot.location);
        setFallbackTrails(trails);
      } catch (e) {
        console.warn('Fallback hiking fetch failed', e);
        setFallbackTrails([]);
      } finally {
        setLoadingFallbackTrails(false);
      }
    };
    run();
  }, [spot?.id, spot?.location, spot?.hikingTrails]);

  useEffect(() => {
    if (!spot) return;
    const roadType = spot.highway && spot.highway !== 'Ukjent' ? String(spot.highway).toUpperCase() : t('shared_spot.unknown_road');
    const surface = spot.surfaceType && spot.surfaceType !== 'Ukjent' ? String(spot.surfaceType) : t('spot.unknown_surface');
    setRouteMeta({ roadType, surface });
  }, [spot]);

  useEffect(() => {
    if (!spot) return;
    setDisplaySpotName(spot.name || t('common.location'));
  }, [spot?.id, spot?.name]);

  useEffect(() => {
    // document.title logic
    const previousTitle = document.title;
    const namePart = (displaySpotName || spot?.name || t('common.location')).trim();
    const locationPart = (address || '').trim();

    if (spot) {
      document.title = `${namePart} | ${locationPart} - Fricamp`;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [spot, displaySpotName, address]);

  // Fetch nearby amenities
  useEffect(() => {
    if (!spot?.location) return;
    const loadAmenities = async () => {
      try {
        const bounds = {
          latMin: spot.location.latitude - 0.05,
          latMax: spot.location.latitude + 0.05,
          lonMin: spot.location.longitude - 0.08,
          lonMax: spot.location.longitude + 0.08
        };
        const near = await getCachedSpotsInArea(bounds);
        const filtered = near.filter(s =>
          ((s.spotType === 'osm_amenity' || s.spotType === 'rasteplass' || (s.features && s.features.some(f => f.includes('UTSIKT'))))) &&
          s.id !== spot.id &&
          getDistance(spot.location.latitude, spot.location.longitude, s.location.latitude, s.location.longitude) <= 2000
        );
        setNearbyAmenities(filtered);
      } catch (e) {
        console.warn('Failed to load nearby amenities', e);
      }
    };
    loadAmenities();
  }, [spot?.id, spot?.location]);


  useEffect(() => {
    const loadWeather = async () => {
      if (!spot?.location) return;
      setWeatherLoading(true);
      try {
        const lat = spot.location.latitude;
        const lon = spot.location.longitude;
        const weatherUrl = `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`;
        const [response, snow] = await Promise.all([
          fetch(weatherUrl, {
            headers: { 'User-Agent': 'fricamp-app github.com/jon-takle' }
          }),
          fetchSnowDepth(lat, lon)
        ]);
        if (!response.ok) throw new Error(`weather_${response.status}`);
        const data = await response.json();
        setSnowData(snow);
        const timeseries = Array.isArray(data?.properties?.timeseries) ? data.properties.timeseries : [];
        const byDate = new Map<string, any[]>();

        timeseries.forEach((ts: any) => {
          const d = new Date(ts.time);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const list = byDate.get(key) || [];
          list.push(ts);
          byDate.set(key, list);
        });

        const locale = getUiLocale();
        const next7: WeatherDay[] = Array.from(byDate.entries())
          .slice(0, 7)
          .map(([key, entries], idx) => {
            const temps = entries
              .map((e: any) => e?.data?.instant?.details?.air_temperature)
              .filter((t: any) => typeof t === 'number') as number[];
            const midday = entries.find((e: any) => new Date(e.time).getHours() >= 12) || entries[0];
            const symbol =
              midday?.data?.next_12_hours?.summary?.symbol_code ||
              midday?.data?.next_6_hours?.summary?.symbol_code ||
              midday?.data?.next_1_hours?.summary?.symbol_code ||
              'cloudy';
            const date = new Date(entries[0].time);
            return {
              key,
              label: idx === 0 ? '__today__' : idx === 1 ? '__tomorrow__' : date.toLocaleDateString(locale, { weekday: 'short' }),
              maxTemp: temps.length ? Math.round(Math.max(...temps)) : 0,
              minTemp: temps.length ? Math.round(Math.min(...temps)) : 0,
              symbol
            };
          });
        setWeather7d(next7);

        const times = SunCalc.getTimes(new Date(), lat, lon);
        const sunrise = times.sunrise;
        const sunset = times.sunset;
        const daylightMs = Math.max(0, sunset.getTime() - sunrise.getTime());
        setSunData({
          sunrise: sunrise.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          sunset: sunset.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          daylightHours: Math.round(daylightMs / (1000 * 60 * 60))
        });
      } catch (e) {
        console.warn('Shared weather fetch failed', e);
        setWeather7d([]);
        setSunData(null);
        setSnowData(null);
      } finally {
        setWeatherLoading(false);
      }
    };
    loadWeather();
  }, [spot?.id, spot?.location]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.id || !spot.location || summarySpotIdsRef.current.has(spot.id)) return;
      // Bom/forbudt-sone/attraksjon skal ikke få AI-generert campingbeskrivelse.
      if (!shouldAutoGenerateSpotDescription(spot)) return;
      const description = spot?.aiResult?.description || spot?.description || '';
      const needsSummary = !description || /analyserer|waiting for analysis|venter pa analyse|analyzing/i.test(description);
      if (!needsSummary) return;
      summarySpotIdsRef.current.add(spot.id);
      try {
        const generated = await generateSpotShareSummary({
          spotName: spot.name,
          highway: spot.highway,
          surfaceType: spot.surfaceType,
          nearestHouse: spot.nearestHouse,
          score: spot.score,
          features: spot.features,
          municipality: (spot as any)?.property?.kommunenavn,
          county: (spot as any)?.property?.fylkesnavn
        });
        const finalSummary = (generated && generated.trim()) ? generated.trim() : getSpotDescriptionFallback(spot);

        const updatedSpot: CampingSpot = {
          ...spot,
          description: finalSummary,
          descriptionAuto: true,
          aiResult: { ...(spot.aiResult || {} as any), description: finalSummary } as any
        };
        setSpot(updatedSpot);

        // Save to both collections if they might exist. descriptionAuto: true er
        // påkrevd — uten flagget freder berikelsen (descriptionIsReplaceable) den
        // auto-genererte teksten som om en eier skrev den.
        const updateData = {
          description: finalSummary,
          descriptionAuto: true,
          aiResult: { ...(spot.aiResult || {} as any), description: finalSummary }
        };

        const promises = [
          setDoc(doc(db, 'cached_spots', spot.id), updateData, { merge: true })
        ];

        // We only update 'spots' if it's a confirmed spot (sourceLevel 4 or confirmed type)
        if (spot.sourceLevel === 4 || spot.spotType === 'confirmed') {
          promises.push(setDoc(doc(db, 'spots', spot.id), updateData, { merge: true }));
        }

        await Promise.all(promises);
      } catch (e) {
        console.warn('Failed to generate/persist spot summary', e);
      }
    };
    run();
  }, [spot]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.id || nameSpotIdsRef.current.has(spot.id)) return;
      const current = (spot.name || '').trim();
      const needsName = !current || /lokasjon|punkt|unnamed|unavngitt|spot/i.test(current);
      if (!needsName) return;
      nameSpotIdsRef.current.add(spot.id);
      try {
        const generated = await generateSpotName({
          address,
          municipality: (spot as any)?.property?.kommunenavn,
          county: (spot as any)?.property?.fylkesnavn,
          highway: spot.highway,
          surfaceType: spot.surfaceType,
          features: spot.features
        });
        if (!generated) return;
        setDisplaySpotName(generated);
        setSpot({ ...spot, name: generated });
        await setDoc(doc(db, 'cached_spots', spot.id), { name: generated }, { merge: true });
      } catch (e) {
        console.warn('Could not generate spot name', e);
      }
    };
    run();
  }, [spot, address]);

  useEffect(() => {
    const run = async () => {
      if (!spot?.id || namedTrailSpotIdsRef.current.has(spot.id)) return;

      const source = (spot.hikingTrails && spot.hikingTrails.length > 0) ? spot.hikingTrails : fallbackTrails;
      if (!source || source.length === 0) return;

      const needsUpdate = source.some(t => isUnnamedTrailName(t.name) || !t.estimatedTime);
      if (!needsUpdate) return;

      namedTrailSpotIdsRef.current.add(spot.id);

      try {
        const updated = await Promise.all(source.map(async (trail, idx) => {
          let newName = trail.name;
          if (isUnnamedTrailName(trail.name)) {
            const generated = await generateTrailName({
              spotName: spot.name,
              municipality: (spot as any)?.property?.kommunenavn,
              county: (spot as any)?.property?.fylkesnavn,
              lengthKm: typeof trail.totalLength === 'number' ? trail.totalLength : undefined,
              difficulty: trail.difficulty,
              trailType: trail.type
            });
            const fallbackName = `${spot.name || 'Fricamp'} - tur ${idx + 1}`;
            newName = generated || fallbackName;
          }

          let estTime = trail.estimatedTime;
          if (!estTime && typeof trail.totalLength === 'number' && trail.totalLength > 0) {
            const hours = trail.totalLength / 1000 / 3.5;
            const totalMinutes = Math.round(hours * 60);
            const hrs = Math.floor(totalMinutes / 60);
            const mins = totalMinutes % 60;
            estTime = hrs > 0
              ? `${hrs}t ${mins > 0 ? `${mins}min` : ''}`
              : `${mins}min`;
          }

          return { ...trail, name: newName, estimatedTime: estTime };
        }));

        if (spot.hikingTrails && spot.hikingTrails.length > 0) {
          const updatedSpot: CampingSpot = { ...spot, hikingTrails: updated };
          setSpot(updatedSpot);
          // Only update if names changed or times added
          await setDoc(doc(db, 'cached_spots', spot.id), { hikingTrails: updated }, { merge: true });
        } else {
          setFallbackTrails(updated);
          await setDoc(doc(db, 'cached_spots', spot.id), { hikingTrails: updated }, { merge: true });
        }
      } catch (e) {
        console.warn('Trail update failed', e);
      }
    };
    run();
  }, [spot, fallbackTrails]);

  const primaryReviewVideoUrl = useMemo(
    () => String((review as any)?.videoUrl || (review as any)?.videoURL || (review as any)?.video || '').trim(),
    [review]
  );

  const externalVideoLinks = useMemo(() => {
    const urls = [primaryReviewVideoUrl, ...reviewVideoUrls].filter(Boolean);
    const seen = new Set<string>();
    const out: string[] = [];
    urls.forEach((u) => {
      const key = String(u).trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      if (!getPreviewableVideo(key)) out.push(key);
    });
    return out;
  }, [primaryReviewVideoUrl, reviewVideoUrls]);

  const mediaSlides = useMemo<MediaSlide[]>(() => {
    const imageSlides: MediaSlide[] = [];
    const videoSlides: MediaSlide[] = [];
    const seen = new Set<string>();
    const hasUserMedia = !!(
      (review?.photoUrl || '').trim() ||
      primaryReviewVideoUrl ||
      (spot?.photoUrls || []).length > 0 ||
      (spot?.photoUrl || '').trim() ||
      (spot?.videoUrl || '').trim() ||
      reviewPhotoUrls.length > 0 ||
      reviewVideoUrls.length > 0
    );

    const pushImage = (url?: string, credit?: { userName?: string; userPhoto?: string; createdAt?: string }) => {
      const normalized = (url || '').trim();
      if (!normalized || seen.has(`img:${normalized}`)) return;
      seen.add(`img:${normalized}`);
      imageSlides.push({
        type: 'image',
        url: normalized,
        creditName: credit?.userName,
        creditPhoto: credit?.userPhoto,
        creditDate: credit?.createdAt || inferDateFromStorageLikeUrl(normalized)
      });
    };

    const pushVideo = (raw?: string) => {
      const normalized = String(raw || '').trim();
      if (!normalized) return;
      const previewable = getPreviewableVideo(normalized);
      if (!previewable) return;
      const key = `${previewable.type}:${previewable.url}`;
      if (seen.has(key)) return;
      seen.add(key);
      if (previewable.type === 'embed') {
        videoSlides.push({ type: 'video-embed', url: previewable.url });
      } else {
        videoSlides.push({ type: 'video-file', url: previewable.url });
      }
    };

    const reviewPhoto = (review?.photoUrl || '').trim();
    pushImage(
      reviewPhoto,
      reviewPhoto
        ? {
          userName: review?.userName || t('route.anonymous'),
          userPhoto: review?.userPhoto || undefined,
          createdAt: Number.isFinite(normalizeTimestampMs(review?.createdAt))
            ? new Date(normalizeTimestampMs(review?.createdAt)).toLocaleDateString(getUiLocale())
            : inferDateFromStorageLikeUrl(reviewPhoto)
        }
        : undefined
    );
    reviewPhotoUrls.forEach((u) => pushImage(u, reviewPhotoCredits[u]));
    (spot?.photoUrls || []).forEach((u) => pushImage(u, reviewPhotoCredits[u]));

    // Add spot-level photo if present
    if (spot?.photoUrl) {
      pushImage(spot.photoUrl, {
        userName: spot.createdByName || t('route.anonymous'),
        userPhoto: spot.createdByPhoto,
        createdAt: Number.isFinite(normalizeTimestampMs(spot.createdAt))
          ? new Date(normalizeTimestampMs(spot.createdAt)).toLocaleDateString(getUiLocale())
          : undefined
      });
    }

    pushVideo(primaryReviewVideoUrl);
    reviewVideoUrls.forEach((u) => pushVideo(u));
    pushVideo(spot?.videoUrl);

    if (!hasUserMedia) {
      const attractionSlides = attractions
        .filter((a) => !!a.thumbnail?.source && !!a.url)
        .slice(0, 4)
        .map((a) => ({
          type: 'attraction-image' as const,
          url: a.thumbnail!.source,
          link: a.url,
          title: a.title
        }));
      attractionSlides.forEach((a) => {
        if (seen.has(`attr:${a.url}`)) return;
        seen.add(`attr:${a.url}`);
        imageSlides.push(a);
      });
    }

    return [...videoSlides, ...imageSlides];
  }, [attractions, primaryReviewVideoUrl, review?.photoUrl, review?.userName, review?.userPhoto, reviewPhotoCredits, reviewPhotoUrls, reviewVideoUrls, spot?.photoUrls, spot?.photoUrl, spot?.videoUrl, spot?.createdByName, spot?.createdByPhoto, spot?.createdAt]);

  useEffect(() => {
    setActiveMediaIndex((prev) => {
      if (mediaSlides.length === 0) return 0;
      return Math.min(prev, mediaSlides.length - 1);
    });
  }, [mediaSlides.length]);

  const goPrevMedia = () => {
    if (mediaSlides.length < 2) return;
    setActiveMediaIndex((prev) => (prev - 1 + mediaSlides.length) % mediaSlides.length);
  };

  const goNextMedia = () => {
    if (mediaSlides.length < 2) return;
    setActiveMediaIndex((prev) => (prev + 1) % mediaSlides.length);
  };

  // Pause auto-rotate when active slide is a video type
  const isActiveSlideVideo = mediaSlides[activeMediaIndex]?.type === 'video-embed' || mediaSlides[activeMediaIndex]?.type === 'video-file';

  // Auto-rotate media every 5 seconds if there's more than one slide and not paused
  useEffect(() => {
    if (mediaSlides.length < 2) return;
    if (mediaPaused || isActiveSlideVideo) return;

    const interval = setInterval(() => {
      goNextMedia();
    }, 5000);

    return () => clearInterval(interval);
  }, [mediaSlides.length, mediaPaused, isActiveSlideVideo]);

  const handleMediaTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    swipeStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  };

  const handleMediaTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (swipeStartXRef.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (typeof endX !== 'number') return;
    const delta = endX - swipeStartXRef.current;
    const SWIPE_THRESHOLD = 45;
    if (delta <= -SWIPE_THRESHOLD) {
      goNextMedia();
    } else if (delta >= SWIPE_THRESHOLD) {
      goPrevMedia();
    }
    swipeStartXRef.current = null;
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-[var(--bg-base)] text-[var(--text-primary)] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 mx-auto border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t('shared_spot.loading')}</p>
        </div>
      </div>
    );
  }

  if (!spot && !loading) {
    return (
      <div className="h-screen w-full bg-[var(--bg-base)] text-[var(--text-primary)] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-3xl p-8 text-center">
          <p className="text-lg font-black text-[var(--text-primary)]">{t('shared_spot.not_found')}</p>
          <p className="text-sm text-[var(--text-secondary)] mt-2">{t('shared_spot.link_expired_text')}</p>
          <a href={withLang('/')} className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 text-white font-black text-xs uppercase tracking-wider">
            <ArrowLeft size={14} />
            {t('common.to_app')}
          </a>
        </div>
      </div>
    );
  }

  const hasMedia = mediaSlides.length > 0;
  const hasUserMedia = !!((review?.photoUrl || '').trim() || primaryReviewVideoUrl || (spot?.photoUrls || []).length > 0 || (spot?.photoUrl || '').trim() || (spot?.videoUrl || '').trim() || reviewPhotoUrls.length > 0 || reviewVideoUrls.length > 0);

  return (
    <div
      className="h-screen w-full overflow-y-auto overflow-x-hidden bg-[#4b5a43] text-[var(--text-primary)] selection:bg-emerald-500/30"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <div className="min-h-full bg-[radial-gradient(circle_at_top,#5a6c4f_0%,#46563f_45%,#32402f_100%)]">
        {/* Header */}
        <div className="max-w-5xl mx-auto px-4 pt-6 md:pt-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <a href={withLang('/')} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-tiny font-black uppercase tracking-wider hover:bg-[var(--bg-surface)] transition-all active:scale-95">
              <ArrowLeft size={14} className="text-emerald-400" />
              {t('common.to_app')}
            </a>
            <button
              onClick={toggleLanguage}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-tiny font-black uppercase tracking-wider hover:bg-[var(--bg-surface)] transition-all active:scale-95"
            >
              <Globe size={14} className="text-emerald-400" />
              {currentLanguage.startsWith('no') ? 'EN' : 'NO'}
            </button>
            {!currentLanguage.startsWith('no') && contentTranslating && (
              <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-500/15 border border-violet-500/30 text-2xs font-bold text-violet-300">
                <Loader2 size={12} className="animate-spin" /> {t('shared.translating')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <a
              href={appStoreUrl || fricampWebUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-14 h-14 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] overflow-hidden flex items-center justify-center hover:scale-105 transition-transform hover:bg-[var(--bg-surface)] hover:border-emerald-500/30 shadow-lg"
              title={appStoreUrl ? t('shared_spot.get_app') : t('shared_spot.visit_fricamp')}
            >
              <img src="/Fricamplogo.png" alt="Fricamp" className="w-full h-full object-cover scale-150 drop-shadow-md" />
            </a>
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-4 mt-6 pb-24 space-y-6">
          <div className="max-w-[760px] mx-auto">
            <h1 className="text-4xl md:text-5xl font-grunge uppercase tracking-wider text-[var(--text-primary)] drop-shadow-lg leading-tight">{effectiveSpotName}</h1>
            <p className="text-xs text-emerald-200/70 mt-1 inline-flex items-center gap-2">
              <MapPin size={12} />
              {(address || '').trim() || t('common.shared_from_fricamp')}
            </p>
            {(() => {
              const muni = spot?.municipality || (spot as any)?.property?.kommunenavn;
              const county = spot?.county || (spot as any)?.property?.fylkesnavn;
              const parts = [muni, county].filter(Boolean);
              return parts.length > 0 ? (
                <p className="text-2xs text-emerald-200/50 mt-0.5">{parts.join(', ')}</p>
              ) : null;
            })()}
          </div>

          <div className="space-y-4">
            {/* Media (Conditional) */}
            {hasMedia && !hasUserMedia && (
              <p className="text-3xs font-black uppercase tracking-widest text-emerald-400/70 mb-1">{t('shared_spot.nearby_places_photos')}</p>
            )}
            {hasMedia && (
              <div
                className="glass-panel rounded-panel overflow-hidden shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-700 relative bg-black min-h-[280px] md:min-h-[480px]"
                onTouchStart={handleMediaTouchStart}
                onTouchEnd={handleMediaTouchEnd}
              >
                <div className="relative h-[280px] md:h-[480px]">
                  {mediaSlides.map((slide, idx) => (
                    <div
                      key={`${slide.type}-${idx}`}
                      className={`absolute inset-0 bg-black transition-all duration-500 ease-out ${idx === activeMediaIndex
                        ? 'opacity-100 scale-100 z-10'
                        : 'opacity-0 scale-[1.02] z-0 pointer-events-none'
                        }`}
                    >
                      {slide.type === 'image' && (
                        <>
                          <img src={slide.url} alt="Review media" className="w-full h-full object-cover" />
                          {(slide.creditName || slide.creditDate) && (
                            <div className="absolute left-3 bottom-3 px-3 py-2 rounded-xl bg-black/65 border border-[var(--border-secondary)] backdrop-blur-sm flex items-center gap-2 z-20">
                              <div className="w-7 h-7 rounded-full overflow-hidden border border-white/25 bg-[var(--bg-input)] flex items-center justify-center">
                                {slide.creditPhoto ? (
                                  <img src={slide.creditPhoto} alt="" className="w-full h-full object-cover" />
                                ) : (
                                  <User size={14} className="text-white/80" />
                                )}
                              </div>
                              <div className="leading-tight">
                                {slide.creditName && (
                                  <div className="text-2xs font-black uppercase tracking-wider text-white/95">
                                    {t('shared_spot.photo_by')} {slide.creditName}
                                  </div>
                                )}
                                {slide.creditDate && (
                                  <div className="text-3xs font-bold tracking-wide text-[var(--text-secondary)]">
                                    {slide.creditDate}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {slide.type === 'attraction-image' && (
                        <a href={slide.link} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full group">
                          <img src={slide.url} alt={slide.title} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 flex flex-col justify-end">
                            <span className="text-3xs font-black uppercase tracking-widest text-emerald-300">{t('spot.attraction')}</span>
                            <span className="text-sm font-bold text-white flex items-center gap-2">
                              {slide.title}
                              <ExternalLink size={12} className="opacity-80" />
                            </span>
                          </div>
                        </a>
                      )}
                      {slide.type === 'video-embed' && (
                        <div
                          className="w-full h-full"
                          onClick={() => setMediaPaused(true)}
                          onFocus={() => setMediaPaused(true)}
                        >
                          <iframe
                            src={slide.url}
                            title={`Video ${idx + 1}`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                          />
                        </div>
                      )}
                      {slide.type === 'video-file' && (
                        <video
                          className="w-full h-full object-cover bg-black"
                          controls
                          preload="metadata"
                          playsInline
                          onPlay={() => setMediaPaused(true)}
                        >
                          <source src={slide.url} />
                        </video>
                      )}
                    </div>
                  ))}
                </div>
                {mediaSlides.length > 1 && (
                  <>
                    <button
                      onClick={goPrevMedia}
                      className="absolute left-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/70"
                      aria-label={t('shared_spot.prev_image')}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={goNextMedia}
                      className="absolute right-3 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/70"
                      aria-label={t('shared_spot.next_image')}
                    >
                      <ChevronRight size={16} />
                    </button>
                    <div className="absolute bottom-3 right-3 z-20 px-2 py-1 rounded-lg bg-black/55 border border-[var(--border-secondary)] text-2xs font-black tracking-wider">
                      {activeMediaIndex + 1}/{mediaSlides.length}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Map */}
            <div className="glass-panel rounded-panel overflow-hidden shadow-2xl w-full max-w-none mx-0 relative min-h-[280px] md:min-h-[480px]" style={{ height: '480px' }}>
              <MapContainer
                key={mapKey}
                center={[spot?.location?.latitude || 61.1373, spot?.location?.longitude || 8.9478]}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
                zoomControl={false}
                maxZoom={21}
                minZoom={4}
              >
                <MapSizeFixer />
                <FitDataBounds points={autoFitPoints} polylines={autoFitPolylines} />
                <ZoomControl position="bottomright" />
                {mapType === 'topo' && (
                  <>
                    <TileLayer url={MAP_LAYERS.NORWAY_TOPO} attribution='&copy; Kartverket' maxZoom={21} maxNativeZoom={18} />
                    <TileLayer url={MAP_LAYERS.TOPO} opacity={0.5} attribution='&copy; OpenTopoMap' maxZoom={21} maxNativeZoom={17} />
                  </>
                )}
                {mapType === 'satellite' && (
                  <>
                    <TileLayer url={MAP_LAYERS.SATELLITE} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                    <TileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                  </>
                )}
                {mapType === 'dark' && (
                  <TileLayer url={MAP_LAYERS.DARK} attribution='&copy; CARTO' />
                )}
                {showMapLayers.spot && spot?.location && (
                  <>
                    <CircleMarker
                      center={[spot.location.latitude, spot.location.longitude]}
                      radius={12}
                      pathOptions={{
                        color: BRAND,
                        fillColor: BRAND,
                        fillOpacity: 0.6,
                        weight: 3,
                        className: 'animate-pulse'
                      }}
                    >
                      {spotRating.count > 0 && (
                        <Tooltip direction="top" offset={[0, -12]} permanent opacity={1}>
                          <span className="text-2xs font-black text-amber-300">★ {spotRating.avg.toFixed(1)}</span>
                        </Tooltip>
                      )}
                      <Popup>
                        <div className="min-w-[180px] text-tiny space-y-1">
                          <div className="font-black">{spot.name || t('common.location')}</div>
                          <div>{address || `${spot.location.latitude.toFixed(5)}, ${spot.location.longitude.toFixed(5)}`}</div>
                          <div className="text-emerald-700 font-semibold">{t('shared.spot_assessed')}</div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  </>
                )}

                {showMapLayers.trails && trailsWithGeometry.map(({ trail, coords }) => (
                  <React.Fragment key={`trail-${trail.id}`}>
                    {/* Visible line */}
                    <Polyline
                      positions={coords}
                      pathOptions={{ color: '#f97316', weight: 4, opacity: 0.9, dashArray: '8 6' }}
                      eventHandlers={{
                        click: (e) => {
                          (e.originalEvent as any)?.stopPropagation?.();
                          setTrailInfoId(trail.id);
                        }
                      }}
                    />
                    {/* Invisible hit-area */}
                    <Polyline
                      positions={coords}
                      pathOptions={{ color: 'transparent', weight: 22, opacity: 0, className: 'leaflet-interactive' }}
                      eventHandlers={{
                        click: (e) => {
                          (e.originalEvent as any)?.stopPropagation?.();
                          setTrailInfoId(trail.id);
                        }
                      }}
                    />
                  </React.Fragment>
                ))}

                {showMapLayers.trails && activeTrailStart && (
                  <Marker position={activeTrailStart} icon={trailEndpointIcon('start')} zIndexOffset={2200}>
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>{t('shared.trail_start')}</Tooltip>
                  </Marker>
                )}
                {showMapLayers.trails && activeTrailEnd && (
                  <Marker position={activeTrailEnd} icon={trailEndpointIcon('end')} zIndexOffset={2200}>
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>{t('shared.trail_end')}</Tooltip>
                  </Marker>
                )}
                {showMapLayers.trails && activeTrailPeak && (
                  <Marker position={activeTrailPeak} icon={trailEndpointIcon('peak')} zIndexOffset={2200}>
                    <Tooltip direction="top" offset={[0, -10]} opacity={1}>{t('shared.trail_peak')}</Tooltip>
                  </Marker>
                )}

                {nearbyAmenities
                  .filter((s) => !!s.location && Number.isFinite(s.location.latitude) && Number.isFinite(s.location.longitude))
                  .map((s) => (
                  <Marker
                    key={`amenity-${s.id}`}
                    position={[s.location.latitude, s.location.longitude]}
                    icon={createCustomIcon(s, false, false)}
                  >
                    <Popup>
                      <div className="min-w-[150px] p-1">
                        <div className="font-black text-xs mb-1">{s.name || t('common.location')}</div>
                        <div className="text-2xs text-[var(--text-muted)] mb-1">{String(s.description || '')}</div>
                        <div className="flex flex-wrap gap-1">
                          {s.features.filter(f => !['OSM', 'NVDB', 'SØKERESULTAT'].includes(f.toUpperCase())).map(f => {
                            const u = f.toUpperCase();
                            let label = f;
                            if (u.includes('BÅLPLASS') || u.includes('FIREPIT')) label = t('shared.firepit');
                            else if (u.includes('UTSIKT') || u.includes('VIEWPOINT')) label = t('shared.viewpoint');
                            else if (u.includes('GAPAHUK') || u.includes('SHELTER')) label = t('shared.shelter');
                            else if (u.includes('VEI-ENDE') || u.includes('ROAD END')) label = t('spot.categories.road_end');
                            else if (u.includes('SKOGSVEI') || u.includes('FOREST ROAD')) label = t('spot.categories.forest_road');
                            else if (u.includes('SNUPLASS') || u.includes('TURNING')) label = t('spot.categories.turning_circle');
                            else if (u.includes('RASTEPLASS') || u.includes('REST AREA')) label = t('spot.categories.rest_area');
                            else if (u.includes('VILLMARK') || u.includes('WILDERNESS') || u.includes('UTMARK')) label = t('spot.wilderness');
                            else if (u.includes('PARKERING') || u === 'PARKING') label = t('spot.categories.parking');
                            else if (f.includes('.') && !f.includes(' ')) { const tr = t(f); if (typeof tr === 'string' && tr !== f) label = tr; }
                            return <span key={f} className="text-3xs px-1.5 py-0.5 bg-stone-100 rounded border border-stone-200">{label}</span>;
                          })}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {showMapLayers.wildlife && groupedWildlife.map((group) => (
                  <CircleMarker
                    key={`wild-group-${group.id}`}
                    center={[group.location.latitude, group.location.longitude]}
                    radius={group.count > 1 ? 10 : 7}
                    pathOptions={{
                      color: '#8b5cf6',
                      fillColor: '#8b5cf6',
                      fillOpacity: 0.35,
                      weight: 2
                    }}
                  >
                    <Tooltip direction="center" permanent opacity={1}>
                      <span className="text-[14px] leading-none">
                        {wildlifeEmoji(group.members[0]?.species)}
                        {group.count > 1 ? ` ${group.count}` : ''}
                      </span>
                    </Tooltip>
                    <Popup>
                      <div className="min-w-[180px] text-tiny space-y-1">
                        <div className="font-black">{group.count > 1 ? t('wildlife_popup.observations') : t('wildlife_popup.observation')}</div>
                        {group.count > 1 && <div>{t('wildlife_popup.cluster_count', { count: group.count })}</div>}
                        {group.members.slice(0, 4).map((m) => (
                          <div key={`obs-${m.id}`} className="border-t border-stone-200 pt-1 mt-1">
                            <div className="font-semibold">{t(`wildlife.${m.species}.name`, m.species)}</div>
                            {m.observedAt && <div>{m.observedAt.toLocaleDateString(getUiLocale())}</div>}
                            <div>{[m.locality, m.municipality].filter(Boolean).join(', ')}</div>
                            {m.detailUrl && (
                              <a href={m.detailUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-600 font-bold">
                                {t('wildlife_popup.source')} <ExternalLink size={10} />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>

              <div className="absolute top-4 left-4 z-[1000] flex flex-wrap gap-2 max-w-[70%]">
                <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-[var(--border-secondary)] text-2xs font-black uppercase tracking-wider text-white">
                  {effectiveSpotName}
                </span>
                <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-emerald-500/30 text-2xs font-black uppercase tracking-wider text-emerald-300">
                  {routeMeta.roadType}
                </span>
                <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-amber-500/30 text-2xs font-black uppercase tracking-wider text-amber-300">
                  {routeMeta.surface}
                </span>
                {spotScore > 0 && (
                  <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-orange-400/40 text-2xs font-black uppercase tracking-wider text-orange-300 inline-flex items-center gap-1">
                    {t('shared_spot.score_label')} {spotScore}
                  </span>
                )}
                {spotRating.count > 0 && (
                  <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-amber-400/40 text-2xs font-black uppercase tracking-wider text-amber-300 inline-flex items-center gap-1">
                    <Star size={11} className="text-amber-300" />
                    {spotRating.avg.toFixed(1)} ({spotRating.count})
                  </span>
                )}
              </div>

              {/* Map Controls */}
              <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-2">
                <button
                  onClick={() => setMapType(prev => prev === 'topo' ? 'satellite' : prev === 'satellite' ? 'dark' : 'topo')}
                  className="w-10 h-10 rounded-xl bg-black/80 backdrop-blur-md border border-[var(--border-secondary)] flex items-center justify-center text-white hover:bg-emerald-500 transition-colors shadow-xl"
                >
                  {mapType === 'topo' ? <Layers size={18} /> : mapType === 'satellite' ? <MapIcon size={18} /> : <Layers size={18} />}
                </button>
                <button
                  onClick={() => setShowMapLayers((prev) => ({ ...prev, spot: !prev.spot }))}
                  className={`w-10 h-10 rounded-xl backdrop-blur-md border flex items-center justify-center transition-colors shadow-xl ${showMapLayers.spot ? 'bg-emerald-600/80 border-emerald-400/40 text-white' : 'bg-black/80 border-[var(--border-secondary)] text-[var(--text-secondary)]'}`}
                  title={t("auto.sharedreviewpage.title.vis_skjul_punkt", "Vis/skjul punkt")}
                >
                  <MapPin size={16} />
                </button>
                <button
                  onClick={() => setShowMapLayers((prev) => ({ ...prev, wildlife: !prev.wildlife }))}
                  className={`w-10 h-10 rounded-xl backdrop-blur-md border flex items-center justify-center transition-colors shadow-xl ${showMapLayers.wildlife ? 'bg-violet-600/80 border-violet-400/40 text-white' : 'bg-black/80 border-[var(--border-secondary)] text-[var(--text-secondary)]'}`}
                  title={t("auto.sharedreviewpage.title.vis_skjul_dyreobservasjoner", "Vis/skjul dyreobservasjoner")}
                >
                  <Bird size={16} />
                </button>
                <button
                  onClick={() => setShowMapLayers((prev) => ({ ...prev, trails: !prev.trails }))}
                  className={`w-10 h-10 rounded-xl backdrop-blur-md border flex items-center justify-center transition-colors shadow-xl ${showMapLayers.trails ? 'bg-orange-600/80 border-orange-400/40 text-white' : 'bg-black/80 border-[var(--border-secondary)] text-[var(--text-secondary)]'}`}
                  title={t('shared_spot.toggle_trails')}
                >
                  <Route size={16} />
                </button>
              </div>

              {activeTrailInfo && (
                <div className="absolute left-4 right-4 md:left-6 md:right-auto md:w-[360px] top-20 z-[1100] bg-black/95 backdrop-blur-xl border border-orange-500/30 rounded-2xl shadow-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[var(--border-secondary)] flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-3xs font-black uppercase tracking-widest text-orange-400">{t('shared_spot.nearby_trails')}</p>
                      <h3 className="text-sm font-black text-[var(--text-primary)] truncate">{activeTrailInfo.trail.name || t('shared_spot.trail_fallback')}</h3>
                    </div>
                    <button
                      onClick={() => setTrailInfoId(null)}
                      className="p-2 rounded-lg bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors"
                      aria-label={t('common.close')}
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <div className="p-4 space-y-2">
                    <div className="text-2xs text-[var(--text-secondary)] font-bold">
                      {activeTrailInfo.trail.type || t('shared_spot.trail_type_fallback')}
                    </div>
                    {typeof activeTrailInfo.trail.distanceToStart === 'number' && (
                      <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                        {t('shared_spot.meters_away', { count: Math.round(activeTrailInfo.trail.distanceToStart) })}
                      </div>
                    )}
                    {activeTrailInfo.trail.difficulty && (
                      <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                        {t('shared.difficulty_label')}{activeTrailInfo.trail.difficulty}
                      </div>
                    )}
                    {typeof activeTrailInfo.trail.totalLength === 'number' && (
                      <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                        {t('shared.length_label')}{formatTrailKm(activeTrailInfo.trail.totalLength)} km
                      </div>
                    )}
                    {activeTrailInfo.trail.estimatedTime && (
                      <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                        {t('shared.estimated_time_label')}{activeTrailInfo.trail.estimatedTime}
                      </div>
                    )}
                    {!activeTrailInfo.trail.estimatedTime && typeof activeTrailInfo.trail.totalLength === 'number' && (
                      <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                        {t('shared.estimated_time_label')}{estimateHikingTime(formatTrailKm(activeTrailInfo.trail.totalLength), activeTrailProfile, t('shared_spot.unknown_time'))}
                      </div>
                    )}
                    {activeTrailInfo.coords.length > 1 && (
                      <>
                        {activeTrailPeak && (
                          <div className="text-2xs text-[var(--text-secondary)] font-bold uppercase tracking-wider">
                            {t('shared_spot.peak_on_map')}
                          </div>
                        )}
                      </>
                    )}
                    {activeTrailInfo.trail.externalUrl && (
                      <a
                        href={activeTrailInfo.trail.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-orange-500/15 border border-orange-500/40 text-orange-300 hover:bg-orange-500/25 transition-colors text-2xs font-black uppercase tracking-widest"
                      >
                        <ExternalLink size={12} />
                        {activeTrailInfo.trail.externalLabel || t('shared_spot.open_external')}
                      </a>
                    )}
                    {loadingTrailElevationId === activeTrailInfo.trail.id && (
                      <div className="pt-2 text-2xs text-[var(--text-muted)] font-bold uppercase tracking-wider flex items-center gap-2">
                        <Loader2 size={12} className="animate-spin" />
                        {t('shared_spot.loading_elevation')}
                      </div>
                    )}
                    {activeTrailProfile && activeTrailProfile.length >= 2 && (
                      <div className="pt-2">
                        <ElevationProfile profile={activeTrailProfile} />
                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 max-w-[760px] mx-auto gap-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300">
            <div className="space-y-6">
              {/* Spot Facts */}
              <div className="bg-[var(--bg-surface)] backdrop-blur-md rounded-2xl border border-[var(--border-primary)] p-5 animate-in fade-in slide-in-from-bottom-4 duration-700">
                <h3 className="text-2xs font-black uppercase tracking-widest text-emerald-400/80 mb-4 flex items-center gap-2">
                  <CheckCircle2 size={12} /> {t('shared_spot.facts_title')}
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-start text-sm">
                    <span className="text-[var(--text-muted)]">{t('common.location')}</span>
                    <span className="text-[var(--text-primary)] font-medium text-right ml-4">{effectiveSpotName}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[var(--text-muted)]">{t('shared_spot.address')}</span>
                    <span className="text-[var(--text-primary)] font-medium text-right ml-4">{address || t('shared_spot.loading_address')}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[var(--text-muted)]">{t('shared_spot.type')}</span>
                    <span className="text-[var(--text-primary)] font-medium capitalize">{spot?.spotType?.replace('_', ' ') || t('common.unknown')}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[var(--text-muted)]">{t('shared_spot.status')}</span>
                    <span className={`font-black text-2xs uppercase tracking-wider px-2 py-0.5 rounded ${spot?.isVerified ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                      {spot?.isVerified
                        ? (verificationMeta.by || verificationMeta.when
                          ? t('shared_spot.verified_by', { details: [verificationMeta.by, verificationMeta.when].filter(Boolean).join(', ') })
                          : t('spot.verified'))
                        : t('shared_spot.unverified')}
                    </span>
                  </div>
                  {spot?.aiResult?.ground_owner && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[var(--text-muted)]">{t('shared_spot.landowner')}</span>
                      <span className="text-[var(--text-primary)] font-medium">{String(spot.aiResult.ground_owner)}</span>
                    </div>
                  )}
                  {spot?.aiResult?.elevation_moh && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[var(--text-muted)]">{t('spot.elevation_label')}</span>
                      <span className="text-[var(--text-primary)] font-medium">{Math.round(spot.aiResult.elevation_moh)} moh</span>
                    </div>
                  )}
                  <div className="border-t border-[var(--border-secondary)] pt-3">
                    <div className="text-2xs font-black uppercase tracking-widest text-emerald-300/80 mb-2">{t('spot.details.attractions.about_location')}</div>
                    <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                      {effectiveDescription || getSpotDescriptionFallback(spot)}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-[var(--border-secondary)]">
                    <button
                      onClick={() => setShowLegal(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/25 text-tiny font-black uppercase tracking-widest text-emerald-400 hover:bg-emerald-500/20 transition-all"
                    >
                      <Gavel size={14} />
                      {t('spot.legal.rules_and_allemannsretten')}
                    </button>
                    <p className="text-2xs text-[var(--text-muted)] mt-3 leading-relaxed text-center italic">
                      {t('common.legal_disclaimer')}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <span className="px-2 py-1 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-3xs font-bold text-emerald-400 uppercase tracking-tighter">
                      {routeMeta.roadType}
                    </span>
                    <span className="px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-3xs font-bold text-amber-300 uppercase tracking-tighter">
                      {routeMeta.surface}
                    </span>
                    {spot?.isVerified && (
                      <span className="px-2 py-1 rounded-lg bg-blue-500/10 border border-blue-500/20 text-3xs font-bold text-blue-400 uppercase tracking-tighter">
                        {t('spot.verified')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Weather + sun */}
              <div className="bg-[var(--bg-surface)] backdrop-blur-md rounded-2xl border border-[var(--border-primary)] p-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:50ms]">
                <h3 className="text-2xs font-black uppercase tracking-widest text-emerald-400/80 mb-3 flex items-center gap-2">
                  <CloudSun size={12} /> {t('weather.title_7d')}
                </h3>
                {weatherLoading && (
                  <div className="text-tiny text-[var(--text-secondary)] italic">{t('weather.loading_weather')}</div>
                )}
                {!weatherLoading && weather7d.length === 0 && (
                  <div className="text-tiny text-[var(--text-secondary)] italic">{t('weather.unavailable_weather')}</div>
                )}
                {weather7d.length > 0 && (
                  <div className="grid grid-cols-7 gap-1">
                    {weather7d.map((day) => (
                      <div key={day.key} className="min-w-0 rounded-xl border border-[var(--border-secondary)] bg-gradient-to-b from-emerald-500/18 to-sky-900/22 px-2 py-2 shadow-lg">
                        <div className="text-3xs font-black uppercase tracking-tight text-white/90 truncate">{day.label === '__today__' ? t('shared.today') : day.label === '__tomorrow__' ? t('shared.tomorrow') : day.label}</div>
                        <div className="mt-1.5 flex items-center justify-center">
                          <WeatherSymbolIcon symbol={day.symbol} animated size={22} />
                        </div>
                        <div className="mt-1 text-center">
                          <div className="text-[13px] font-black text-white leading-none">{day.maxTemp}°</div>
                          <div className="text-2xs font-bold text-sky-200/85 leading-none mt-0.5">{day.minTemp}°</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sunData && (
                  <div className="mt-3 rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] px-3 py-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-tiny text-[var(--text-secondary)]"><Sunrise size={13} className="text-amber-400" /> {sunData.sunrise}</div>
                    <div className="text-2xs font-black uppercase tracking-widest text-amber-300">{t('weather.daylight_hours_interp', { count: sunData.daylightHours })}</div>
                    <div className="flex items-center gap-1.5 text-tiny text-[var(--text-secondary)]"><Sunset size={13} className="text-orange-400" /> {sunData.sunset}</div>
                    {snowData && snowData.depthCm > 0 && (
                      <div
                        className="flex items-center gap-2 border-l border-[var(--border-secondary)] pl-2"
                        title={t('weather.snow')}
                      >
                        <div className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300/35 bg-sky-400/15 px-2 py-1">
                          <CloudSnow size={13} className="text-sky-100" />
                          <span className="text-3xs font-black uppercase tracking-wider text-sky-100">{t('weather.snow')}</span>
                          <span className="text-[12px] font-black text-white">{Math.round(snowData.depthCm)} cm</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="bg-[var(--bg-surface)] backdrop-blur-md rounded-2xl border border-[var(--border-primary)] p-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:100ms]">
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => handleSelectInfoTab('wildlife')}
                    className={`px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border transition-colors ${activeInfoTab === 'wildlife' ? 'bg-violet-500/20 text-violet-300 border-violet-500/40' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border-[var(--border-secondary)]'}`}
                  >
                    {t('sidebar.safari')} ({wildlifeCount})
                  </button>
                  <button
                    onClick={() => handleSelectInfoTab('trails')}
                    className={`px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border transition-colors ${activeInfoTab === 'trails' ? 'bg-orange-500/20 text-orange-300 border-orange-500/40' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border-[var(--border-secondary)]'}`}
                  >
                    {t('sidebar.hiking')} ({trailsCount})
                  </button>
                  <button
                    onClick={() => handleSelectInfoTab('attractions')}
                    className={`px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest border transition-colors ${activeInfoTab === 'attractions' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border-[var(--border-secondary)]'}`}
                  >
                    {t('sidebar.attractions')} ({attractionsCount})
                  </button>
                </div>

                {activeInfoTab === 'wildlife' && (
                  <div className="space-y-2">
                    {wildlifePoints.length === 0 && (
                      <div className="text-tiny text-[var(--text-secondary)] italic">{t('sidebar.no_observations')}</div>
                    )}
                    {wildlifePoints.slice(0, 20).map((obs) => {
                      const expanded = expandedWildlifeIds.has(obs.id);
                      return (
                        <div key={`wild-row-${obs.id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                          <button
                            onClick={() => setExpandedWildlifeIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(obs.id)) next.delete(obs.id); else next.add(obs.id);
                              return next;
                            })}
                            className="w-full px-3 py-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-2">
                              <Bird size={13} className="text-violet-300" />
                              <span className="text-sm font-bold text-[var(--text-primary)]">{obs.species}</span>
                            </div>
                            {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                          </button>
                          {expanded && (
                            <div className="px-3 py-2 text-[12px] text-[var(--text-secondary)] space-y-1">
                              {obs.observedAt && <div>{t('common.date')}: {obs.observedAt.toLocaleDateString(currentLanguage.startsWith('no') ? 'no-NO' : 'en-US')}</div>}
                              <div>{t('common.location')}: {[obs.locality, obs.municipality, obs.county].filter(Boolean).join(', ') || t('common.unknown')}</div>
                              {typeof obs.observedCount === 'number' && <div>{t('common.count_label')}: {obs.observedCount}</div>}
                              {obs.detailUrl && (
                                <a href={obs.detailUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-300 font-bold">
                                  {t('common.source')} <ExternalLink size={11} />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {activeInfoTab === 'trails' && (
                  <div className="space-y-2">
                    {loadingFallbackTrails && allNearbyTrails.length === 0 && (
                      <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared_spot.loading_trails')}</div>
                    )}
                    {!loadingFallbackTrails && allNearbyTrails.length === 0 && (
                      <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared_spot.empty_trails')}</div>
                    )}
                    {allNearbyTrails.map((trail) => {
                      const expanded = expandedTrailIds.has(trail.id);
                      return (
                        <div key={`trail-row-${trail.id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                          <button
                            onClick={() => setExpandedTrailIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(trail.id)) next.delete(trail.id); else next.add(trail.id);
                              return next;
                            })}
                            className="w-full px-3 py-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-2">
                              <Route size={13} className="text-orange-300" />
                              <span className="text-sm font-bold text-[var(--text-primary)]">{trail.name || t('shared_spot.trail_fallback')}</span>
                            </div>
                            {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                          </button>
                          {expanded && (
                            <div className="px-3 py-2 text-[12px] text-[var(--text-secondary)] space-y-1">
                              {trail.difficulty && <div>{t('shared.difficulty_label')}{trail.difficulty}</div>}
                              {typeof trail.totalLength === 'number' && <div>{t('shared.length_label')}{formatTrailKm(trail.totalLength)} km</div>}
                              {trail.estimatedTime && <div>{t('shared.estimated_time_label')}{trail.estimatedTime}</div>}
                              {typeof trail.distanceToStart === 'number' && <div>{t('shared.distance_to_start')}{(trail.distanceToStart / 1000).toFixed(1)} km</div>}
                              {trail.externalUrl && (
                                <a href={trail.externalUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-300 font-bold">
                                  {t('shared_spot.open_external')} <ExternalLink size={11} />
                                </a>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {activeInfoTab === 'attractions' && (
                  <div className="space-y-2">
                    {loadingAttractions && (
                      <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared_spot.loading_attractions')}</div>
                    )}
                    {!loadingAttractions && attractions.length === 0 && (
                      <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared_spot.empty_attractions_nearby')}</div>
                    )}
                    {attractions.map((attr) => {
                      const expanded = expandedAttractionIds.has(attr.pageid);
                      const ew = getEffectiveWiki(attr);
                      return (
                        <div key={`attr-row-${attr.pageid}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                          <button
                            onClick={() => setExpandedAttractionIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(attr.pageid)) next.delete(attr.pageid); else next.add(attr.pageid);
                              return next;
                            })}
                            className="w-full px-3 py-2 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <TreePine size={13} className="text-emerald-300 shrink-0" />
                              <span className="text-sm font-bold text-[var(--text-primary)] truncate">{ew.title}</span>
                            </div>
                            {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                          </button>
                          {expanded && (
                            <div className="px-3 py-2 text-[12px] text-[var(--text-secondary)] space-y-2">
                              {(ew.thumbnail?.source || attr.thumbnail?.source) && (
                                <img src={ew.thumbnail?.source || attr.thumbnail!.source} alt={ew.title} className="w-full h-32 object-cover rounded-lg border border-[var(--border-secondary)]" />
                              )}
                              {ew.extract && <div>{ew.extract}</div>}
                              <a href={ew.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-emerald-300 font-bold">
                                {t('shared_spot.open_external')} <ExternalLink size={11} />
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div >

          {/* Conditionally show Review or generic footer */}
          <div className="glass-panel p-6 md:p-8 rounded-panel max-w-5xl mx-auto">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                {review?.userPhoto ? (
                  <img src={review.userPhoto} className="w-12 h-12 rounded-2xl object-cover border border-[var(--border-secondary)]" alt={review.userName || 'User'} />
                ) : (
                  <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <span className="text-lg font-black text-emerald-400">
                      {review?.userName?.charAt(0) || 'F'}
                    </span>
                  </div>
                )}
                <div>
                  <h2 className="text-lg font-black text-[var(--text-primary)] leading-tight">{review?.userName || 'Fricamp'}</h2>
                  <div className="flex items-center gap-1 mt-1 text-2xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
                    <span>{t('shared_spot.reviewed_label')}</span>
                    <span className="w-1 h-1 rounded-full bg-[var(--bg-input)]" />
                    <span>{review?.createdAt ? new Date(review.createdAt).toLocaleDateString(currentLanguage.startsWith('no') ? 'no-NO' : currentLanguage.startsWith('de') ? 'de-DE' : 'en-GB') : t('shared_spot.recently')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)]">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star key={s} size={14} fill={s <= (review ? review.rating : (spotScore > 0 ? spotScore / 2 : 0)) ? '#fbbf24' : 'none'} className={s <= (review ? review.rating : (spotScore > 0 ? spotScore / 2 : 0)) ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                ))}
              </div>
            </div>

            <div className="mt-8 relative italic">
              <div className="absolute -top-4 -left-4 text-emerald-500/20 text-4xl font-serif">"</div>
              <p className="text-lg md:text-xl text-[var(--text-primary)] font-medium leading-relaxed relative z-10">
                {effectiveReviewComment || effectiveDescription || t('shared_spot.no_description')}
              </p>
            </div>

            {
              externalVideoLinks.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {externalVideoLinks.map((link, idx) => (
                    <a
                      key={`${link}-${idx}`}
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors text-2xs font-black uppercase tracking-widest"
                    >
                      <ExternalLink size={13} />
                      {t('shared.video')}
                    </a>
                  ))}
                </div>
              )
            }
          </div >
        </main >

        {/* Floating Action Bar (Mobile Only) */}
        < div className="md:hidden fixed bottom-6 left-6 right-6 z-mapOverlay animate-in slide-in-from-bottom-8 duration-1000" >
          <div className="grid grid-cols-2 gap-3">
            {spot?.location ? (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${spot.location.latitude},${spot.location.longitude}&travelmode=driving`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-14 rounded-2xl bg-orange-500 text-white font-black text-tiny uppercase tracking-[0.12em] shadow-2xl flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <MapPin size={14} />
                {t('shared_spot.navigate_to_spot')}
              </a>
            ) : (
              <div className="h-14 rounded-2xl bg-black/50 text-[var(--text-secondary)] font-black text-tiny uppercase tracking-[0.12em] flex items-center justify-center">
                {t('shared_spot.navigate_to_spot')}
              </div>
            )}
            <a
              href={appStoreUrl || withLang('/')}
              target={appStoreUrl ? '_blank' : undefined}
              rel={appStoreUrl ? 'noopener noreferrer' : undefined}
              className="h-14 rounded-2xl bg-emerald-600 text-white font-black text-tiny uppercase tracking-[0.12em] shadow-2xl flex items-center justify-center gap-2 active:scale-95 transition-all"
            >
              {appStoreUrl ? t('shared_spot.get_app') : t('shared_spot.open_in_app')}
            </a>
          </div>
        </div >
        <div className="pb-6 pt-2 text-center">
          <a
            href="https://www.takleconsulting.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-2xs text-white/45 hover:text-white/70 transition-colors"
          >
            {t('shared_spot.made_by')}
          </a>
        </div>
      </div >

      {showLegal && <LegalGuide onClose={() => setShowLegal(false)} />}
    </div >
  );
};

function isUnnamedTrailName(name?: string): boolean {
  if (!name || !name.trim()) return true;
  const normalized = name.trim().toLowerCase();
  return normalized.includes('unnamed') || normalized.includes('unavngitt') || normalized === 'tur' || normalized === 'sti';
}

function formatTrailKm(length: number): string {
  if (!Number.isFinite(length)) return '0.0';
  // Some data sources return meters, others kilometers.
  const km = length > 80 ? length / 1000 : length;
  return km.toFixed(1);
}

function extractTrailCoords(trail: HikingTrail): [number, number][] {
  const raw = (trail as any)?.geometry || (trail as any)?.path || (trail as any)?.coordinates || [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((p: any) => {
      if (Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])) {
        const a = Number(p[0]);
        const b = Number(p[1]);
        return Math.abs(a) <= 90 && Math.abs(b) <= 180 ? [a, b] as [number, number] : [b, a] as [number, number];
      }
      const lat = Number(p?.latitude ?? p?.lat);
      const lon = Number(p?.longitude ?? p?.lon ?? p?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return [lat, lon] as [number, number];
    })
    .filter((p: [number, number] | null): p is [number, number] => p !== null);
}

const WeatherSymbolIcon: React.FC<{ symbol: string; animated?: boolean; size?: number }> = ({ symbol, animated = false, size = 16 }) => {
  const s = (symbol || '').toLowerCase();
  const motion = animated ? 'animate-pulse' : '';
  if (s.includes('clearsky') || s.includes('fair')) return <Sun size={size} className={`text-amber-300 ${motion}`} />;
  if (s.includes('partlycloudy')) return <CloudSun size={size} className={`text-amber-200 ${motion}`} />;
  if (s.includes('thunder')) return <CloudLightning size={size} className={`text-yellow-300 ${motion}`} />;
  if (s.includes('snow') || s.includes('sleet')) return <CloudSnow size={size} className={`text-sky-200 ${motion}`} />;
  if (s.includes('rain')) return <CloudRain size={size} className={`text-sky-300 ${motion}`} />;
  if (s.includes('fog')) return <CloudFog size={size} className={`text-[var(--text-secondary)] ${motion}`} />;
  return <Cloud size={size} className={`text-[var(--text-primary)] ${motion}`} />;
};

function compactWeatherLabel(symbol: string, translate: (key: string) => string): string {
  const s = (symbol || '').toLowerCase();
  if (s.includes('clearsky')) return translate('weather.clearsky');
  if (s.includes('fair')) return translate('weather.fair');
  if (s.includes('partlycloudy')) return translate('weather.partly_cloudy');
  if (s.includes('cloudy')) return translate('weather.cloudy');
  if (s.includes('thunder')) return translate('weather.thunder');
  if (s.includes('snow')) return translate('weather.snow_label');
  if (s.includes('sleet')) return translate('weather.sleet');
  if (s.includes('rain')) return translate('weather.rain');
  if (s.includes('fog')) return translate('weather.fog');
  return translate('weather.weather_generic');
}

function wildlifeEmoji(species?: string): string {
  const s = (species || '').toLowerCase();
  if (s.includes('ørn') || s.includes('eagle')) return '🦅';
  if (s.includes('ugle') || s.includes('owl')) return '🦉';
  if (s.includes('elg') || s.includes('moose')) return '🫎';
  if (s.includes('hjort') || s.includes('deer') || s.includes('rådyr')) return '🦌';
  if (s.includes('rein') || s.includes('reindeer')) return '🦌';
  if (s.includes('rev') || s.includes('fox')) return '🦊';
  if (s.includes('bjørn') || s.includes('bear')) return '🐻';
  if (s.includes('ulv') || s.includes('wolf')) return '🐺';
  if (s.includes('gaupe') || s.includes('lynx')) return '🐆';
  if (s.includes('jerv') || s.includes('wolverine')) return '🐾';
  if (s.includes('hare') || s.includes('rabbit')) return '🐇';
  if (s.includes('bever') || s.includes('beaver')) return '🦫';
  if (s.includes('oter') || s.includes('otter')) return '🦦';
  if (s.includes('sel') || s.includes('seal')) return '🦭';
  if (s.includes('hval') || s.includes('whale')) return '🐋';
  if (s.includes('fisk') || s.includes('trout') || s.includes('laks') || s.includes('salmon')) return '🐟';
  if (s.includes('fugl') || s.includes('bird')) return '🐦';
  return '🐾';
}

function getUiLocale(): string {
  const raw = (window.localStorage.getItem('i18nextLng') || 'no').toLowerCase();
  if (raw.startsWith('en')) return 'en-US';
  return 'no-NO';
}

const trailEndpointIcon = (type: 'start' | 'end' | 'peak') => {
  const tone = type === 'start' ? '#16a34a' : type === 'end' ? '#dc2626' : '#f59e0b';
  const label = type === 'start' ? 'S' : type === 'end' ? 'E' : 'M';
  return L.divIcon({
    className: `trail-${type}-icon`,
    html: `<div style="width:26px;height:26px;border-radius:9999px;background:${tone};border:2px solid #fff;color:#fff;font-weight:900;font-size:12px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.35)">${label}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 13]
  });
};

function estimateHikingTime(lengthKmText: string, profile?: ElevationPoint[], unknownLabel = 'ukjent'): string {
  const km = Number(lengthKmText);
  if (!Number.isFinite(km) || km <= 0) return unknownLabel;
  let gain = 0;
  if (profile && profile.length > 1) {
    for (let i = 1; i < profile.length; i++) {
      const diff = profile[i].elevation - profile[i - 1].elevation;
      if (diff > 0) gain += diff;
    }
  }
  const hours = km / 4 + gain / 600;
  const whole = Math.floor(hours);
  const mins = Math.round((hours - whole) * 60);
  if (whole <= 0) return `${Math.max(20, mins)} min`;
  return `${whole}t ${String(mins).padStart(2, '0')}m`;
}

function normalizeTimestampMs(value: any): number {
  if (!value) return NaN;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') {
    const ns = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
    return value.seconds * 1000 + Math.floor(ns / 1_000_000);
  }
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function inferDateFromStorageLikeUrl(url?: string): string | undefined {
  if (!url) return undefined;
  try {
    // Works for Firebase Storage download URLs where path is encoded in /o/<path>.
    const parsed = new URL(url);
    const pathMatch = parsed.pathname.match(/\/o\/(.+)$/);
    const encodedPath = pathMatch?.[1];
    if (!encodedPath) return undefined;
    const decoded = decodeURIComponent(encodedPath);
    const fileName = decoded.split('/').pop() || '';
    const tsMatch = fileName.match(/(?:^|_)(\d{13})(?:_|$)/);
    if (!tsMatch) return undefined;
    const ms = Number(tsMatch[1]);
    if (!Number.isFinite(ms) || ms < 946684800000) return undefined;
    return new Date(ms).toLocaleDateString(getUiLocale());
  } catch {
    return undefined;
  }
}

function getPreviewableVideo(videoUrl?: string): { type: 'embed' | 'file'; url: string } | null {
  if (!videoUrl) return null;
  let urlToParse = videoUrl;
  if (!urlToParse.match(/^https?:\/\//i)) {
    urlToParse = `https://${urlToParse}`;
  }

  try {
    const url = new URL(urlToParse);
    const host = url.hostname.toLowerCase();

    if (host.includes('youtube.com')) {
      const id =
        url.searchParams.get('v') ||
        (url.pathname.includes('/shorts/') ? url.pathname.split('/shorts/')[1]?.split('/')[0] : null) ||
        (url.pathname.includes('/live/') ? url.pathname.split('/live/')[1]?.split('/')[0] : null);
      if (!id) return null;
      return { type: 'embed', url: `https://www.youtube.com/embed/${id}` };
    }
    if (host.includes('youtu.be')) {
      let id = url.pathname.replace('/', '').trim();
      if (id.endsWith('/')) id = id.slice(0, -1);
      if (!id) return null;
      return { type: 'embed', url: `https://www.youtube.com/embed/${id}` };
    }
    if (host.includes('vimeo.com')) {
      const id = url.pathname.split('/').filter(Boolean).pop();
      if (!id) return null;
      return { type: 'embed', url: `https://player.vimeo.com/video/${id}` };
    }

    if (/\.(mp4|webm|ogg|mov|m3u8)(\?.*)?$/i.test(url.pathname)) {
      return { type: 'file', url: urlToParse };
    }
    return null;
  } catch {
    return null;
  }
}

async function getDetailedAddress(coords: { latitude: number; longitude: number }): Promise<string> {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.latitude}&lon=${coords.longitude}&addressdetails=1&zoom=18`;
  const res = await fetch(url, { headers: { Accept: 'application/json', 'Accept-Language': 'no,nb,nn,en;q=0.5' } });
  if (!res.ok) throw new Error(`address_${res.status}`);
  const data = await res.json();
  const addr = data?.address || {};

  const roadPart = [addr.road, addr.house_number].filter(Boolean).join(' ').trim();
  const localityPart = [addr.postcode, addr.city || addr.town || addr.village || addr.hamlet].filter(Boolean).join(' ').trim();
  const areaPart = [addr.suburb || addr.neighbourhood, addr.municipality, addr.county].filter(Boolean).join(', ').trim();
  const countryPart = addr.country || '';

  const composed = [roadPart, localityPart, areaPart, countryPart].filter(Boolean).join(', ').trim();
  if (composed) return composed;
  if (typeof data?.display_name === 'string' && data.display_name.trim()) return data.display_name.trim();
  return `${coords.latitude.toFixed(5)}, ${coords.longitude.toFixed(5)}`;
}

class LocalErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('SharedReviewPage Render Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center">
          <div className="max-w-2xl w-full bg-red-900/20 border border-red-500/50 p-6 rounded-2xl">
            <h2 className="text-xl font-bold mb-4">{i18n.t("auto.sharedreviewpage.react_render_crash", "React Render Crash")}</h2>
            <p className="text-sm text-red-300 font-mono overflow-auto max-h-40">
              {this.state.error?.message}
            </p>
            <p className="text-2xs text-[var(--text-muted)] mt-4 uppercase tracking-widest font-black">
              {i18n.t("auto.sharedreviewpage.check_console_logs_for_stack_t", "Check console logs for stack trace")}
            </p>
            <button onClick={() => window.location.reload()} className="mt-6 px-4 py-2 bg-red-500 text-white rounded-lg text-xs font-bold uppercase tracking-wider">
              {i18n.t("auto.sharedreviewpage.last_inn_pa_nytt", "Last inn på nytt")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const SharedReviewPageWithErrorBoundary: React.FC<SharedReviewPageProps> = (props) => (
  <LocalErrorBoundary>
    <SharedReviewPage {...props} />
  </LocalErrorBoundary>
);

export default SharedReviewPageWithErrorBoundary;
